// src/theme/index.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  createGlobalStyle,
  ThemeProvider as SCThemeProvider,
} from "styled-components";

/* ---------- Design tokens (base) ---------- */
const base = {
  fonts: {
    ui: "'Inter', 'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
    mono: "'Fira Code', 'Source Code Pro', monospace",
  },
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
    xxl: "48px",
  },
  radii: {
    sm: "6px",
    md: "12px",
    lg: "24px",
  },
  shadows: {
    card: "0px 8px 24px rgba(2,6,23,0.6)",
  },
};

/* ---------- Themes ---------- */
export const darkTheme = {
  ...base,
  name: "dark",
  colors: {
    primary: "#3B82F6",
    primaryDark: "#1E3A8A",
    accent: "#60A5FA",

    bg: "#02040A",
    bgElevated: "#070B18",
    cardBg: "#0B1020",
    border: "#1A2238",

    text: "#E2E8F0",
    textSecondary: "#94A3B8",
    textMuted: "#64748B",

    success: "#22C55E",
    warning: "#FACC15",
    danger: "#EF4444",
    info: "#0EA5E9",
  },
};

export const lightTheme = {
  ...base,
  name: "light",
  colors: {
    primary: "#3B82F6",
    primaryDark: "#1E3A8A",
    accent: "#60A5FA",

    bg: "#FFFFFF",
    bgElevated: "#F8FAFC",
    cardBg: "#FFFFFF",
    border: "#E2E8F0",

    text: "#0F172A",
    textSecondary: "#475569",
    textMuted: "#94A3B8",

    success: "#16A34A",
    warning: "#F59E0B",
    danger: "#DC2626",
    info: "#0284C7",
  },
};

/* ---------- GlobalStyle (safe for JS) ---------- */
export const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; }
  html, body, #root { height: 100%; }
  body {
    margin: 0;
    font-family: ${({ theme }) =>
      theme && theme.fonts ? theme.fonts.ui : "Inter, sans-serif"};
    background: ${({ theme }) =>
      theme && theme.colors ? theme.colors.bg : "#02040A"};
    color: ${({ theme }) =>
      theme && theme.colors ? theme.colors.text : "#E2E8F0"};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.6;
  }

  :root {
    --skilll-primary: ${({ theme }) => theme?.colors?.primary};
    --skilll-accent: ${({ theme }) => theme?.colors?.accent};
    --bg: ${({ theme }) => theme?.colors?.bg};
    --card-bg: ${({ theme }) => theme?.colors?.cardBg};
    --text: ${({ theme }) => theme?.colors?.text};
  }

  a { color: ${({ theme }) => theme?.colors?.primary}; text-decoration: none; }

  ::-webkit-scrollbar { width: 10px; height:10px; }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme?.colors?.border};
    border-radius: 999px;
  }
`;

/* ---------- Theme Provider (JS) ---------- */
const STORAGE_KEY = "skilll_theme";

const ThemeContext = createContext({
  themeName: "dark",
  toggleTheme: () => {},
  setTheme: () => {},
});

export const AppThemeProvider = ({ children }) => {
  // safe initial (handles SSR-ish scenarios)
  const getInitial = () => {
    try {
      if (typeof window === "undefined") return "dark";
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored || "dark";
    } catch {
      return "dark";
    }
  };

  const [themeName, setThemeName] = useState(getInitial);

  useEffect(() => {
    try {
      if (typeof window !== "undefined")
        window.localStorage.setItem(STORAGE_KEY, themeName);
    } catch {}
  }, [themeName]);

  const toggleTheme = () =>
    setThemeName((t) => (t === "dark" ? "light" : "dark"));
  const setTheme = (t) => setThemeName(t);

  const theme = useMemo(
    () => (themeName === "dark" ? darkTheme : lightTheme),
    [themeName]
  );

  return (
    <ThemeContext.Provider value={{ themeName, toggleTheme, setTheme }}>
      <SCThemeProvider theme={theme}>
        <GlobalStyle />
        {children}
      </SCThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
